export function initParticles(canvas) {
  if (!canvas) return () => {};

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) return () => {};

  const ctx = canvas.getContext('2d');
  let w = 0;
  let h = 0;
  let frameId = 0;

  const particles = [];
  const particleCount = 40;

  const COLORS = {
    cyan: '100, 210, 205',
    green: '70, 180, 150',
    blue: '50, 120, 180',
    teal: '45, 150, 160',
  };

  function resize() {
    w = canvas.width = window.innerWidth;
    h = canvas.height = window.innerHeight;
  }

  function createParticle() {
    const hues = ['cyan', 'green', 'teal', 'blue'];
    return {
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 2.4 + 0.5,
      dx: (Math.random() - 0.5) * 0.14,
      dy: (Math.random() - 0.5) * 0.1 - 0.03,
      phase: Math.random() * Math.PI * 2,
      pulse: Math.random() * 0.015 + 0.004,
      hue: hues[Math.floor(Math.random() * hues.length)],
    };
  }

  function init() {
    resize();
    particles.length = 0;
    for (let i = 0; i < particleCount; i += 1) {
      particles.push(createParticle());
    }
  }

  function waveY(x, t, layer) {
    const slow = t * 0.00008;
    const amp = 90 + layer * 40;
    const base = h * (0.12 + layer * 0.16);
    return (
      base
      + Math.sin(x * 0.0014 + slow + layer * 1.7) * amp
      + Math.sin(x * 0.0038 - slow * 1.2 + layer * 0.5) * (amp * 0.5)
      + Math.cos(x * 0.0008 + slow * 0.7) * (amp * 0.3)
    );
  }

  function drawSkyWash(t) {
    const driftX = Math.sin(t * 0.00007) * w * 0.12;
    const driftY = Math.cos(t * 0.00005) * h * 0.06;

    const topGrad = ctx.createLinearGradient(0, 0, 0, h);
    topGrad.addColorStop(0, 'rgba(15, 45, 75, 0.22)');
    topGrad.addColorStop(0.4, 'rgba(25, 70, 90, 0.1)');
    topGrad.addColorStop(0.75, 'rgba(8, 12, 18, 0)');
    topGrad.addColorStop(1, 'rgba(8, 12, 18, 0)');
    ctx.fillStyle = topGrad;
    ctx.fillRect(0, 0, w, h);

    const cx = w * 0.5 + driftX;
    const cy = h * 0.38 + driftY;
    const radius = Math.max(w, h) * 0.85;
    const centerGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    centerGlow.addColorStop(0, 'rgba(80, 190, 200, 0.09)');
    centerGlow.addColorStop(0.35, 'rgba(40, 120, 130, 0.07)');
    centerGlow.addColorStop(0.7, 'rgba(20, 50, 90, 0.05)');
    centerGlow.addColorStop(1, 'rgba(8, 12, 18, 0)');
    ctx.fillStyle = centerGlow;
    ctx.fillRect(0, 0, w, h);

    const sideX = w * 0.85 + Math.sin(t * 0.00009) * w * 0.08;
    const sideGlow = ctx.createRadialGradient(sideX, h * 0.2, 0, sideX, h * 0.2, h * 0.55);
    sideGlow.addColorStop(0, 'rgba(50, 140, 120, 0.08)');
    sideGlow.addColorStop(1, 'rgba(8, 12, 18, 0)');
    ctx.fillStyle = sideGlow;
    ctx.fillRect(0, 0, w, h);
  }

  function drawAurora(t) {
    const layers = [
      { rgb: COLORS.blue, alpha: 0.11 },
      { rgb: COLORS.cyan, alpha: 0.09 },
      { rgb: COLORS.teal, alpha: 0.08 },
      { rgb: COLORS.green, alpha: 0.06 },
    ];

    ctx.globalCompositeOperation = 'lighter';

    layers.forEach((layer, i) => {
      ctx.beginPath();
      ctx.moveTo(0, h);
      for (let x = 0; x <= w; x += 5) {
        ctx.lineTo(x, waveY(x, t, i));
      }
      ctx.lineTo(w, h);
      ctx.closePath();

      const peakY = h * (0.08 + i * 0.12);
      const grad = ctx.createLinearGradient(0, peakY, 0, h);
      grad.addColorStop(0, `rgba(${layer.rgb}, ${layer.alpha * 0.4})`);
      grad.addColorStop(0.25, `rgba(${layer.rgb}, ${layer.alpha})`);
      grad.addColorStop(0.65, `rgba(${layer.rgb}, ${layer.alpha * 0.6})`);
      grad.addColorStop(1, `rgba(${layer.rgb}, 0)`);
      ctx.fillStyle = grad;
      ctx.fill();

      ctx.beginPath();
      for (let x = 0; x <= w; x += 5) {
        if (x === 0) ctx.moveTo(x, waveY(x, t, i));
        else ctx.lineTo(x, waveY(x, t, i));
      }
      for (let x = w; x >= 0; x -= 5) {
        ctx.lineTo(x, waveY(x, t, i) - 60 - i * 20);
      }
      ctx.closePath();

      const ribbonGrad = ctx.createLinearGradient(0, peakY, 0, peakY + h * 0.35);
      ribbonGrad.addColorStop(0, `rgba(${layer.rgb}, 0)`);
      ribbonGrad.addColorStop(0.5, `rgba(${layer.rgb}, ${layer.alpha * 0.55})`);
      ribbonGrad.addColorStop(1, `rgba(${layer.rgb}, 0)`);
      ctx.fillStyle = ribbonGrad;
      ctx.fill();
    });

    ctx.globalCompositeOperation = 'source-over';
  }

  function drawHorizon(t) {
    const drift = Math.sin(t * 0.00012) * 0.012;
    const horizonY = h * (0.56 + drift);

    const glow = ctx.createLinearGradient(0, horizonY - h * 0.25, 0, h);
    glow.addColorStop(0, 'rgba(80, 190, 200, 0)');
    glow.addColorStop(0.2, 'rgba(40, 100, 140, 0.09)');
    glow.addColorStop(0.55, 'rgba(60, 180, 170, 0.16)');
    glow.addColorStop(0.85, 'rgba(80, 200, 190, 0.1)');
    glow.addColorStop(1, 'rgba(30, 80, 100, 0.08)');

    ctx.fillStyle = glow;
    ctx.fillRect(0, horizonY - h * 0.22, w, h * 0.5);

    ctx.save();
    ctx.strokeStyle = 'rgba(100, 220, 210, 0.14)';
    ctx.lineWidth = 1.5;
    ctx.shadowColor = 'rgba(80, 200, 190, 0.4)';
    ctx.shadowBlur = 14;
    ctx.beginPath();
    ctx.moveTo(0, horizonY);
    ctx.lineTo(w, horizonY);
    ctx.stroke();
    ctx.restore();
  }

  function drawGrid(t) {
    const horizonY = h * 0.56 + Math.sin(t * 0.00012) * h * 0.012;
    const vanishX = w * 0.5 + Math.sin(t * 0.0001) * w * 0.1;
    const vanishY = horizonY - h * 0.01;
    const lines = 18;

    const gridFade = ctx.createLinearGradient(0, vanishY, 0, h);
    gridFade.addColorStop(0, 'rgba(80, 200, 190, 0.03)');
    gridFade.addColorStop(0.15, 'rgba(80, 200, 190, 0.1)');
    gridFade.addColorStop(1, 'rgba(100, 220, 210, 0.16)');

    ctx.save();
    ctx.strokeStyle = gridFade;
    ctx.lineWidth = 1;

    for (let i = 1; i <= lines; i += 1) {
      const progress = i / lines;
      const y = vanishY + (h - vanishY) * progress * progress;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }

    const verticals = 22;
    for (let i = -verticals; i <= verticals; i += 1) {
      const spread = (i / verticals) * w * 1.6;
      ctx.beginPath();
      ctx.moveTo(vanishX + spread * 0.015, vanishY);
      ctx.lineTo(vanishX + spread, h);
      ctx.stroke();
    }

    ctx.restore();
  }

  function drawParticles(t) {
    for (const p of particles) {
      const opacity = 0.07 + Math.sin(t * p.pulse + p.phase) * 0.05;
      const rgb = COLORS[p.hue];

      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r * 3, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${rgb}, ${opacity * 0.4})`;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${rgb}, ${opacity})`;
      ctx.fill();

      p.x += p.dx + Math.sin(t * 0.0004 + p.phase) * 0.025;
      p.y += p.dy + Math.cos(t * 0.0003 + p.phase) * 0.02;

      if (p.x < -20) p.x = w + 20;
      if (p.x > w + 20) p.x = -20;
      if (p.y < -20) p.y = h + 20;
      if (p.y > h + 20) p.y = -20;
    }
  }

  function frame(t) {
    ctx.clearRect(0, 0, w, h);
    drawSkyWash(t);
    drawAurora(t);
    drawHorizon(t);
    drawGrid(t);
    drawParticles(t);
    frameId = requestAnimationFrame(frame);
  }

  window.addEventListener('resize', init);
  init();
  frameId = requestAnimationFrame(frame);

  return () => {
    cancelAnimationFrame(frameId);
    window.removeEventListener('resize', init);
  };
}
